<template>
  <StudioMobile
    v-if="isMobileViewport"
    :ar-modal-open="arModalOpen"
    :ar-model-url="arModelUrl"
    :ar-page-url="arPageUrl"
    :is-exporting-ar="isExportingAR"
    :ar-error="arError"
    :ar-auto-launch="arAutoLaunch"
    :close-ar-modal="closeARModal"
    :handle-ar-retry="handleARRetry"
    @view-in-room="handleARAutoLaunch"
  />
  <NewStudioShell
    v-else
    :ar-modal-open="arModalOpen"
    :ar-model-url="arModelUrl"
    :ar-page-url="arPageUrl"
    :ar-loading="isExportingAR"
    :ar-error="arError"
    :ar-auto-launch="arAutoLaunch"
    @close-ar="closeARModal"
    @retry-ar="handleARRetry"
    @view-in-room="handleARAutoLaunch"
  />
</template>

<script setup>
import {
  DEFAULT_GRID_PATTERN,
  GRID_PATTERN_LIST,
  SELECTABLE_TOOLS,
} from "~/composables/grid/constants";
import {
  clearMiniStudioCookie,
  loadMiniStudioDesign,
} from "~/composables/miniStudio";
import { transformShapeForAdd } from "~/composables/shapes/transform";
import { loadVariations } from "~/composables/shapes/utils";
import { apply3DColors } from "~/composables/use3DColors";
import { SweetAlertIcon } from "~/types/sweetalert";
import { ModalType } from "~/stores/modal";
import { useNewStudioARAutoLaunch } from "~/composables/useNewStudioARAutoLaunch";

definePageMeta({
  layout: "default",
  ssr: true,
});

// $toast is undefined during SSR (sweetalert2 is client-only) — only call in onMounted/handlers
const { $toast } = useNuxtApp();
const route = useRoute();
const gridStore = useGridStore();
const projectStore = useProjectStore();
const loadingStore = useLoadingStore();
const modalStore = useModalStore();

const { isMobile } = useDeviceDetection();

// Track viewport for mobile/desktop UI swap.
// Phones (window.innerWidth < 768) get the old mobile UI; everything else gets the
// desktop editor. Width-only — height alone doesn't make a device "phone": a desktop
// window with a short viewport (e.g. 1920x720 or 150%-scaled 1366x720) should still
// see the desktop UI; its right panel scrolls internally below the 825px
// --studio-workspace-height target.
//
// useViewportMode resolves this during SSR from the request's User-Agent, so a
// phone gets the mobile shell in the first HTML response instead of the
// desktop shell being painted and then swapped after hydration.
const { isMobileViewport } = useViewportMode();

const showColorRemapModal = (design) => {
  if (design?.remappedColors?.length) {
    modalStore.toggleModal(ModalType.ColorRemap, {
      options: { remappedColors: design.remappedColors },
    });
  }
};

const {
  arModalOpen,
  arModelUrl,
  arPageUrl,
  arError,
  isExportingAR,
  arAutoLaunch,
  handleARAutoLaunch,
  closeARModal,
  handleARRetry,
  cleanupAR,
} = useNewStudioARAutoLaunch();

// SSR pre-fetch: load design data server-side when ID is known
const designCookie = useCookie("studio_design_id");
const routeDesignId = computed(() => {
  const id = route.query.id;
  return id ? String(id).split(".")[0] : null;
});
const preloadId = computed(() => routeDesignId.value || designCookie.value);
const preloadKey = computed(
  () => `new-studio-design:${preloadId.value || "empty"}`,
);

const { data: preloadedDesign } = await useAsyncData(
  preloadKey,
  () =>
    preloadId.value
      ? $fetch(`/api/designs/${preloadId.value}`)
      : Promise.resolve(null),
  { server: true, lazy: true },
);

onUnmounted(() => cleanupAR());

onMounted(async () => {
  try {
    loadingStore.setLoading(true);
    const shouldOpenStartModal =
      !route.query.id &&
      !route.query.import &&
      !designCookie.value &&
      !route.query["mini-studio"];
    if (shouldOpenStartModal) {
      modalStore.toggleModal(ModalType.New);
    }
    const { fetchColors } = useStudioColors();
    await Promise.all([loadVariations(), fetchColors()]);
    const previousDesignId = designCookie.value;

    if (route.query["mini-studio"] === "true") {
      try {
        const miniStudioData = await loadMiniStudioDesign();
        if (miniStudioData) {
          const squarePattern =
            GRID_PATTERN_LIST.find((p) => p.id === "square") ||
            GRID_PATTERN_LIST[DEFAULT_GRID_PATTERN];
          gridStore.setGridPattern(squarePattern);
          gridStore.setGridSize(
            miniStudioData.gridSize.width,
            miniStudioData.gridSize.height,
          );
          gridStore.shapeList = await Promise.all(
            miniStudioData.shapes.map(async (shape) =>
              transformShapeForAdd(shape),
            ),
          );
          gridStore.setSelectedColor(miniStudioData.colorChoice);
          await projectStore.save(true, true);
          clearMiniStudioCookie();
          $toast.fire({
            icon: SweetAlertIcon.SUCCESS,
            text: "Mini studio design loaded and saved successfully!",
          });
        } else {
          throw new Error("No mini studio design data found");
        }
      } catch (miniStudioError) {
        console.error("Failed to load mini studio design:", miniStudioError);
        gridStore.setGridPattern(GRID_PATTERN_LIST[DEFAULT_GRID_PATTERN]);
        $toast.fire({
          icon: SweetAlertIcon.ERROR,
          text: "Failed to load mini studio design. Starting with empty canvas.",
        });
      }
    } else if (route.query.id) {
      const [designId] = route.query.id.split(".");
      if (preloadedDesign.value) {
        await gridStore.loadDesign(preloadedDesign.value);
        const d = preloadedDesign.value;
        const canonicalId = d.id || designId;
        projectStore.setDetails(
          canonicalId,
          d.design_name,
          d.grid_properties?.backgroundColor,
          d.can_feature,
          d.commercial_project,
          d.project_name,
          d.salesperson,
          d.author_name,
          d.author_email,
        );
        setCookie("studio_design_id", canonicalId);
        if (canonicalId !== designId) {
          await navigateTo(
            { query: { ...route.query, id: canonicalId } },
            { replace: true },
          );
        }
        showColorRemapModal(preloadedDesign.value);
      } else {
        const design = await projectStore.load(designId);
        if (design) {
          await gridStore.loadDesign(design);
          setCookie("studio_design_id", designId);
          showColorRemapModal(design);
        }
      }
    } else if (route.query.import) {
      const importDesignId = route.query.import;
      const colorsParam = route.query.colors;
      try {
        await $fetch("/api/designs/increment-import-count", {
          method: "POST",
          body: { id: importDesignId },
        });
      } catch (error) {
        console.error("Failed to track import:", error);
      }
      const design = await projectStore.load(importDesignId, true, true);
      if (design) {
        await gridStore.loadDesign(design);
        setCookie("studio_design_id", design.id);
        showColorRemapModal(design);
        if (colorsParam) {
          await nextTick();
          const updatedShapes = await apply3DColors(
            importDesignId,
            colorsParam,
            gridStore.shapeList,
            typeof route.query.set_ids === 'string' ? {
              setIds: route.query.set_ids.split(','),
              engravings: typeof route.query.engravings === 'string' ? route.query.engravings.split(',') : [],
            } : undefined,
          );
          if (updatedShapes) {
            gridStore.shapeList = updatedShapes;
            gridStore.updateShapeList3D();
            await projectStore.save(false, true);
          }
        }
      }
    } else if (previousDesignId) {
      if (preloadedDesign.value) {
        await gridStore.loadDesign(preloadedDesign.value);
        projectStore.setDesignId(previousDesignId);
        showColorRemapModal(preloadedDesign.value);
      } else {
        const design = await projectStore.load(previousDesignId);
        if (design) {
          await gridStore.loadDesign(design);
          showColorRemapModal(design);
        }
      }
      await navigateTo({ query: { id: previousDesignId } }, { replace: true });
    } else {
      gridStore.setGridPattern(GRID_PATTERN_LIST[DEFAULT_GRID_PATTERN]);
    }

    // AR Auto-Launch from QR Code Scan (?ar=true)
    if (route.query.ar === "true" && gridStore.shapeList.length > 0) {
      console.log(
        "[AR] Detected ar=true parameter, initiating AR auto-launch...",
      );
      await nextTick();
      setTimeout(() => handleARAutoLaunch(), 500);
    }
  } catch (e) {
    if (e.message === "Design not found") {
      await navigateTo("/", { replace: true });
      deleteCookie("studio_design_id");
      gridStore.setGridPattern(GRID_PATTERN_LIST[DEFAULT_GRID_PATTERN]);
      modalStore.toggleModal(ModalType.New);
      $toast.fire({ icon: SweetAlertIcon.WARNING, text: "Design not found" });
    } else {
      $toast.fire({ icon: SweetAlertIcon.ERROR, text: "Error loading design" });
      console.warn(e);
    }
  } finally {
    if (
      gridStore.selectedShape &&
      gridStore.selectedColor &&
      gridStore.tempNewItems.length === 0
    ) {
      gridStore.setTempItemByCurrentSelection();
    }
    if (isMobile.value) {
      gridStore.updateSelectedTool(SELECTABLE_TOOLS.MOVE_GRID);
    } else if (projectStore.designId) {
      gridStore.updateSelectedTool(SELECTABLE_TOOLS.SELECT);
    } else {
      gridStore.updateSelectedTool(SELECTABLE_TOOLS.ADD);
    }
    loadingStore.setLoading(false);
  }
});
</script>

<style scoped>
/* The mobile studio's shell height.

   This rule also matches the mobile studio's root element: Vue's scoped CSS
   gives a child component's ROOT element the parent's scope attribute, and
   <StudioMobile> is rendered directly by this page. Its own .pageWrapper
   rule is in components/StudioMobile.vue.

   Both must agree, and both must use the VISIBLE viewport (100svh):
   `-webkit-fill-available` resolves to the LARGE viewport on iOS Safari (the
   height with the browser bar collapsed), so the page laid out ~100px taller
   than the visible area, the footer was pushed behind Safari's bottom bar,
   and the canvas grey showed through the strip below it as a grey band under
   "Add to Cart".

   The `-webkit-fill-available` fallback is declared FIRST so that `100svh`
   wins the cascade where it is supported. */
.pageWrapper {
  /* mobile viewport bug fix (legacy WebKit) */
  min-height: -webkit-fill-available;
  height: 100svh;
  min-height: 100svh;
  position: fixed;
  width: 100%;
  left: 0;
  top: 0;
}

#sideBar {
  width: 280px;
}

#headerTools {
  height: 120px;
}

#mainContent {
  background: #a2a2a2;
}
</style>
